import React from 'react';
import { useServiceAuth } from '../../hooks';
import { ServiceAuth } from '../../hooks/useService/useService';
import FhirService from '../../services/FhirService';

const documentReferenceFhirService = new FhirService('DocumentReference');

const decodeBase64Utf8 = (value: string) => {
  const normalizedValue = value.replace(/\s/g, '');
  if (
    !normalizedValue ||
    normalizedValue.length % 4 !== 0 ||
    /[^A-Za-z0-9+/=]/.test(normalizedValue)
  ) {
    return undefined;
  }
  try {
    const binaryString = window.atob(normalizedValue);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    return typeof TextDecoder !== 'undefined'
      ? new TextDecoder('utf-8').decode(bytes)
      : binaryString;
  } catch {
    return undefined;
  }
};

const summaryMarkdown = (document?: fhir.DocumentReference) => {
  const attachment = document?.content?.find(
    item =>
      item.attachment?.contentType?.split(';')[0]?.trim().toLowerCase() ===
      'text/markdown',
  )?.attachment;
  if (!attachment?.data) {
    return undefined;
  }
  return decodeBase64Utf8(attachment.data)?.trim() || undefined;
};

export type MemberExtractionSummaryResult = {
  markdown?: string;
  isLoading: boolean;
  failed: boolean;
};

export const useMemberExtractionSummary = (
  summaryReference?: string,
): MemberExtractionSummaryResult => {
  const auth = useServiceAuth();
  const [markdown, setMarkdown] = React.useState<string>();
  const [isLoading, setIsLoading] = React.useState(Boolean(summaryReference));
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const account = auth?.account;
    const id = summaryReference?.split('/')[1];
    if (!summaryReference || !account || !auth?.authToken || !id) {
      setMarkdown(undefined);
      setIsLoading(false);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setMarkdown(undefined);
    setIsLoading(true);
    setFailed(false);
    documentReferenceFhirService
      .getById(auth as ServiceAuth)(account, id)
      .then(response => {
        if (!cancelled) {
          setMarkdown(summaryMarkdown(response.data as fhir.DocumentReference));
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [auth, summaryReference]);

  return { markdown, isLoading, failed };
};
